import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { type FormEvent, useRef, useState } from 'react'
import { ErrorBanner } from '@/components/app/error-banner'
import { LogoMark } from '@/components/app/logo-mark'
import { SubmitButton } from '@/components/app/submit-button'
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp'
import { apiPost } from '@/lib/api'
import { getApiErrorMessage } from '@/lib/api-error'
import { getAuthFlow, updateAuthFlow } from '@/lib/auth-flow'
import { redirectIfAuthenticated } from '@/lib/guards'

export const Route = createFileRoute('/connexion/code')({
  // Le tunnel vit en mémoire : un rechargement renvoie à l'étape 1.
  beforeLoad: () => {
    // L'ordre compte : authentifié d'abord, sinon on renvoie vers /connexion
    // pour cause de tunnel vide alors que l'écran attendu est son espace.
    redirectIfAuthenticated()
    if (!getAuthFlow()?.phone) throw redirect({ to: '/connexion' })
  },
  component: OtpScreen,
})

interface VerifyOtpResponse {
  otpToken: string
  needsPin: boolean
}

const OTP_LENGTH = 6

function OtpScreen() {
  const navigate = useNavigate()
  const phone = getAuthFlow()?.phone ?? ''

  const [code, setCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [resending, setResending] = useState(false)
  // Évite un double envoi quand l'auto-submit et le clic partent ensemble.
  const submitting = useRef(false)

  async function verify(value: string) {
    if (value.length !== OTP_LENGTH || submitting.current) return

    submitting.current = true
    setError(null)
    setLoading(true)

    try {
      const res = await apiPost<VerifyOtpResponse>('auth/mobile/verify-otp', {
        phone,
        code: value,
      })
      updateAuthFlow({ otpToken: res.otpToken, needsPin: res.needsPin })
      await navigate({ to: '/connexion/pin' })
    } catch (err) {
      setError(getApiErrorMessage(err, 'Code invalide ou expiré.'))
      setCode('')
    } finally {
      submitting.current = false
      setLoading(false)
    }
  }

  async function handleResend() {
    if (resending) return
    setError(null)
    setResending(true)

    try {
      await apiPost('auth/mobile/request-otp', { phone })
      setCode('')
    } catch (err) {
      setError(getApiErrorMessage(err, 'Impossible de renvoyer le code.'))
    } finally {
      setResending(false)
    }
  }

  function handleSubmit(event: FormEvent) {
    event.preventDefault()
    void verify(code)
  }

  return (
    <div className="flex min-h-dvh flex-col justify-center bg-navy-950 px-6 pt-safe pb-safe">
      <div className="mx-auto w-full max-w-sm">
        <LogoMark caption="VÉRIFICATION" />

        <form onSubmit={handleSubmit} className="mt-10 flex flex-col gap-5">
          <div>
            <h2 className="text-[20px] font-semibold text-chalk">Entrez le code reçu</h2>
            <p className="mt-1.5 text-[13px] text-steel">
              Envoyé au <span className="font-semibold text-gold">{phone}</span>
            </p>
          </div>

          <div className="flex justify-center">
            <InputOTP
              maxLength={OTP_LENGTH}
              value={code}
              onChange={setCode}
              onComplete={verify}
              autoFocus
              disabled={loading}
              aria-label="Code de vérification à 6 chiffres"
            >
              <InputOTPGroup>
                {Array.from({ length: OTP_LENGTH }, (_, i) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: position fixe
                  <InputOTPSlot key={i} index={i} />
                ))}
              </InputOTPGroup>
            </InputOTP>
          </div>

          <ErrorBanner message={error} />

          <SubmitButton disabled={code.length !== OTP_LENGTH} loading={loading}>
            Valider
          </SubmitButton>

          <button
            type="button"
            onClick={handleResend}
            disabled={resending}
            className="text-center text-[14px] text-gold disabled:opacity-40"
          >
            {resending ? 'Envoi en cours…' : 'Renvoyer le code'}
          </button>
        </form>
      </div>
    </div>
  )
}
